import { batch, createEffect, createRoot } from 'solid-js';
import { unwrap } from 'solid-js/store';

import {
	type AtpAccessJwt,
	type AtpSessionData,
	type AuthLoginOptions,
	CredentialManager,
	type FetchHandler,
	type FetchHandlerObject,
	XRPC,
	buildFetchHandler,
} from '@atcute/client';
import type { At } from '@atcute/client/lexicons';
import { mergeHeaders } from '@atcute/client/utils/http';
import { decodeJwt } from '@atcute/client/utils/jwt';

import { getCurrentAppView } from '~/api/globals/appview';
import { type Session as OAuthSession, OAuthUserAgent, getSession } from '~/api/oauth';

import { signal } from '~/utils/signals';
import { createReactiveLocalStorage } from '~/utils/storage';

export interface MultiagentLoginOptions extends AuthLoginOptions {
	service: string;
}

export interface MultiagentProfileData {
	displayName?: string;
	// handle: string;
	avatar?: string;
	indexedAt?: string;
}

export interface MultiagentAccountData {
	readonly did: At.DID;
	service: string;
	session: AtpSessionData;
	scope?: 'limited' | 'privileged';
	/**
	 * Authentication method. `undefined` means a legacy app-password / credential
	 * session; `'oauth'` means tokens are managed by the OAuth client and the
	 * `session` field only carries display metadata (handle, did, pds).
	 */
	type?: 'oauth';
	profile?: MultiagentProfileData;
}

interface MultiagentStorage {
	$version: 2;
	active: At.DID | undefined;
	accounts: MultiagentAccountData[];
}

export interface AgentInstance {
	rpc: XRPC;
	auth: CredentialManager | OAuthUserAgent;
}

interface StoredAgent extends AgentInstance {
	p: Promise<AgentInstance>;
	c: () => void;
}

export class MultiagentError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = 'MultiagentError';
	}
}

export class Multiagent {
	store: MultiagentStorage;
	services = signal<Labeler[]>([]);

	#agents: Record<At.DID, StoredAgent> = {};

	constructor(name: string) {
		const store = createReactiveLocalStorage<MultiagentStorage>(name, (version, prev) => {
			if (version === 0) {
				const obj: MultiagentStorage = {
					$version: 2,
					active: undefined,
					accounts: [],
				};

				return obj;
			}

			if (version < 2) {
				const _next = prev as MultiagentStorage;

				for (const account of _next.accounts) {
					account.scope = (account as any).isAppPassword ? 'limited' : undefined;
					delete (account as any).isAppPassword;
				}

				_next.$version = 2;
			}

			return prev;
		});

		this.store = store;
	}

	/**
	 * A record of registered accounts
	 */
	get accounts() {
		return this.store.accounts;
	}

	/**
	 * Active UID set as default
	 */
	get active() {
		let value = this.store.active;

		if (value === undefined) {
			const accounts = this.accounts;

			if (accounts.length > 0) {
				value = this.store.active = accounts[0].did;
			}
		}

		return value;
	}
	set active(next: At.DID | undefined) {
		const accounts = this.store.accounts;
		const index = next ? accounts.findIndex((x) => x.did === next) : -1;

		batch(() => {
			this.store.active = index !== -1 ? next : undefined;

			if (index !== -1) {
				const [acc] = accounts.splice(index, 1);
				accounts.unshift(acc);
			}
		});
	}

	/**
	 * Login with a new account
	 */
	async login({ service, ...rest }: MultiagentLoginOptions): Promise<At.DID> {
		const { rpc, auth, c: cleanup } = this.#createAgent(service);

		await auth.login(rest);

		try {
			const session = auth.session!;
			const did = session.did;

			const sessionJwt = decodeJwt(session.accessJwt) as AtpAccessJwt;
			const scope = sessionJwt.scope;

			let accountScope: MultiagentAccountData['scope'];
			if (scope === 'com.atproto.appPass') {
				accountScope = 'limited';
			} else if (scope === 'com.atproto.appPassPrivileged') {
				accountScope = 'privileged';
			}

			batch(() => {
				const $accounts = this.accounts!;
				const existing = $accounts.find((acc) => acc.did === did);

				if (existing) {
					existing.service = service;
					existing.session = session;
					existing.scope = accountScope;
				} else {
					$accounts.push({
						did: did,
						service: service,
						session: session,
						scope: accountScope,
					});
				}
			});

			// We already have an agent instance, so let's just use the existing one.
			// The mutation above should have updated the session data.
			if (this.#agents[did]) {
				cleanup();
				return did;
			}

			const stored: StoredAgent = {
				p: Promise.resolve().then(() => stored),
				c: cleanup,
				rpc,
				auth,
			};

			this.#agents[did as At.DID]?.c();
			this.#agents[did] = stored;
			return did;
		} catch (err) {
			throw new MultiagentError(`LOGIN_FAILURE`, { cause: err });
		}
	}

	/**
	 * Log out from account
	 */
	logout(did: At.DID): void {
		const $accounts = this.accounts;
		const index = $accounts.findIndex((acc) => acc.did === did);

		if (index !== -1) {
			const account = $accounts[index];

			// For OAuth accounts, revoke the tokens and drop the stored session.
			if (account.type === 'oauth') {
				const agent = this.#agents[did];
				const auth = agent?.auth;

				if (auth instanceof OAuthUserAgent) {
					// Fire-and-forget; we don't want logout to hang on the network.
					void auth.signOut().catch(() => {});
				} else {
					void getSession(did, { allowStale: true })
						.then((session) => new OAuthUserAgent(session).signOut())
						.catch(() => {});
				}
			}

			if (did in this.#agents) {
				this.#agents[did].c();
				delete this.#agents[did];
			}

			$accounts.splice(index, 1);

			if (this.active === did) {
				this.active = undefined;
			}
		}
	}

	/**
	 * Retrieve an agent associated with an account
	 */
	connect(did: At.DID): Promise<AgentInstance> {
		if (did in this.#agents) {
			const stored = this.#agents[did];
			return stored.p;
		}

		const $accounts = this.store.accounts;
		const data = $accounts.find((acc) => acc.did === did);

		if (!data) {
			return Promise.reject(new MultiagentError(`INVALID_ACCOUNT`));
		}

		if (data.type === 'oauth') {
			return this.#connectOAuth(did);
		}

		const { rpc, auth, c: cleanup } = this.#createAgent(data.service);

		const promise = new Promise<void>((resolve, reject) => {
			auth.resume(unwrap(data.session)).then(
				() => {
					resolve();
				},
				(err) => {
					cleanup();

					delete this.#agents[did];
					reject(new MultiagentError(`RESUME_FAILURE`, { cause: err }));
				},
			);
		}).then(() => stored);

		const stored: StoredAgent = { p: promise, c: cleanup, rpc, auth };

		this.#agents[did] = stored;
		return stored.p;
	}

	#createAgent(serviceUri: string) {
		let ignore = false;

		const $accounts = this.store.accounts!;

		const auth = new CredentialManager({
			service: serviceUri,
			onRefresh(session) {
				const did = session!.did;
				const existing = $accounts.find((acc) => acc.did === did);

				if (existing) {
					ignore = true;
					batch(() => Object.assign(existing.session, session));
					ignore = false;
				}
			},
		});

		const rpc = new XRPC({ handler: auth });

		return {
			rpc: rpc,
			auth: auth,
			c: createRoot((dispose) => {
				// A bit of a hack, but works right now.
				rpc.handle = attachLabelerHeaders(rpc.handle, () => this.services.value);

				createEffect(() => {
					const actual = auth.session;

					const did = actual?.did;
					const existing = $accounts.find((acc) => did && acc.did === did);

					if (!ignore && existing) {
						const expected = existing.session;

						if (actual!.accessJwt !== expected.accessJwt || actual!.refreshJwt !== expected.refreshJwt) {
							actual!.accessJwt = expected.accessJwt;
							actual!.refreshJwt = expected.refreshJwt;
						}
					}
				});

				return dispose;
			}),
		};
	}

	/**
	 * Register an account authenticated via OAuth. The OAuth tokens themselves are
	 * persisted by the OAuth client; here we only record display metadata so the
	 * account shows up in the account list and switcher.
	 */
	async addOAuthAccount(session: OAuthSession): Promise<At.DID> {
		const did = session.info.sub;

		const { rpc, auth, c: cleanup } = this.#createOAuthAgent(session);

		// Best-effort fetch of the handle/profile for display purposes.
		let handle = 'handle.invalid';
		let profile: MultiagentProfileData | undefined;
		try {
			const response = await rpc.get('app.bsky.actor.getProfile', { params: { actor: did } });
			const data = response.data;

			handle = data.handle;
			profile = {
				displayName: data.displayName,
				avatar: data.avatar,
				indexedAt: data.indexedAt,
			};
		} catch {
			// Ignore; we'll fall back to handle.invalid and refresh later.
		}

		const sessionData: AtpSessionData = {
			did: did,
			handle: handle,
			pdsUri: session.info.aud,
			// OAuth tokens live in the OAuth store, not here.
			accessJwt: '',
			refreshJwt: '',
			active: true,
		};

		batch(() => {
			const $accounts = this.accounts;
			const existing = $accounts.find((acc) => acc.did === did);

			if (existing) {
				existing.service = session.info.aud;
				existing.session = sessionData;
				existing.type = 'oauth';
				existing.scope = undefined;
				if (profile) {
					existing.profile = profile;
				}
			} else {
				$accounts.push({
					did: did,
					service: session.info.aud,
					session: sessionData,
					type: 'oauth',
					profile: profile,
				});
			}
		});

		this.#agents[did]?.c();

		const stored: StoredAgent = {
			p: Promise.resolve().then(() => stored),
			c: cleanup,
			rpc,
			auth,
		};

		this.#agents[did] = stored;
		return did;
	}

	#connectOAuth(did: At.DID): Promise<AgentInstance> {
		let cleanup: (() => void) | undefined;

		// `rpc`/`auth` are populated once the promise resolves; callers always
		// await `connect` before touching them.
		const stored: StoredAgent = {
			p: null as any,
			c: () => cleanup?.(),
			rpc: null as any,
			auth: null as any,
		};

		stored.p = (async () => {
			let session: OAuthSession;

			try {
				// `allowStale` lets us boot instantly with the stored session; the
				// user agent refreshes the access token on the first 401 if needed.
				session = await getSession(did, { allowStale: true });
			} catch (err) {
				delete this.#agents[did];
				throw new MultiagentError(`RESUME_FAILURE`, { cause: err });
			}

			const agent = this.#createOAuthAgent(session);

			cleanup = agent.c;
			stored.rpc = agent.rpc;
			stored.auth = agent.auth;

			return stored;
		})();

		this.#agents[did] = stored;
		return stored.p;
	}

	#createOAuthAgent(session: OAuthSession) {
		const auth = new OAuthUserAgent(session);
		const rpc = new XRPC({ handler: auth });

		return {
			rpc: rpc,
			auth: auth,
			c: createRoot((dispose) => {
				rpc.handle = attachLabelerHeaders(rpc.handle, () => this.services.value);
				return dispose;
			}),
		};
	}
}

interface Labeler {
	did: At.DID;
	redact?: boolean;
}

const attachLabelerHeaders = (
	handler: FetchHandler | FetchHandlerObject,
	labelers: () => Labeler[],
): FetchHandler => {
	const next = buildFetchHandler(handler);

	return (pathname, init) => {
		return next(pathname, {
			...init,
			headers: mergeHeaders(init.headers, {
				'atproto-accept-labelers': labelers()
					.map((labeler) => labeler.did + (labeler.redact ? `;redact` : ``))
					.join(', '),
				// Route authenticated requests through the user's currently selected
				// AppView. The PDS forwards proxied requests to this service DID.
				// Resolved per request so AppView switches apply without reconnecting.
				'atproto-proxy': getCurrentAppView().did,
			}),
		});
	};
};
